// src/services/schools.js
import { db } from "./firebase";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs,
  query as q, where, orderBy, limit as qLimit,
  serverTimestamp, Timestamp, setDoc
} from "firebase/firestore";

import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signOut,
} from "firebase/auth";

export const schoolsCol = collection(db, "schools");

/* ──────────────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────────────── */

/** Convert mixed inputs to Firestore Timestamp (or null) */
const toTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === "function") return value;
  if (typeof value === "object" && typeof value.seconds === "number") {
    const d = new Date(value.seconds * 1000);
    return isNaN(d) ? null : Timestamp.fromDate(d);
  }
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
};

/** Deep-remove undefined values (keep nulls) */
const cleanUndefined = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const out = Array.isArray(obj) ? [] : {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = typeof v === "object" && v !== null ? cleanUndefined(v) : v;
  }
  return out;
};

/** Normalize a school payload before writing to Firestore */
export const normalizeSchoolPayload = (payload = {}) => {
  const next = { ...payload };

  if (next.plan) next.plan = String(next.plan).toLowerCase();
  if (next.code) next.code = String(next.code).toUpperCase();

  if (next.subscription) {
    const rd = next.subscription.renewalDate;
    next.subscription = {
      ...next.subscription,
      renewalDate: rd ? toTimestamp(rd) : null,
    };
  }

  return cleanUndefined(next);
};

/* ──────────────────────────────────────────────────────────────────
   CRUD
   ────────────────────────────────────────────────────────────────── */

/** CREATE school doc */
export async function createSchool(payload) {
  const docData = {
    ...normalizeSchoolPayload(payload),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  return addDoc(schoolsCol, docData); // => DocumentReference
}

/** UPDATE (partial) */
export async function updateSchool(id, payload) {
  const ref = doc(db, "schools", id);
  const patch = {
    ...normalizeSchoolPayload(payload),
    updatedAt: serverTimestamp(),
  };
  return updateDoc(ref, patch);
}

/** DELETE (hard) */
export async function removeSchool(id) {
  return deleteDoc(doc(db, "schools", id));
}

/** Read one */
export async function getSchool(id) {
  const snap = await getDoc(doc(db, "schools", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** List with basic filters */
export async function listSchools({
  activeOnly,
  plan,
  state,
  orderByField = "createdAt",
  order = "desc",
  limit = 50,
} = {}) {
  const clauses = [];
  if (activeOnly) clauses.push(where("active", "==", true));
  if (plan) clauses.push(where("plan", "==", String(plan).toLowerCase()));
  if (state) clauses.push(where("state", "==", state));

  const qry = q(schoolsCol, ...clauses, orderBy(orderByField, order), qLimit(limit));
  const snap = await getDocs(qry);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Update only the logo url */
export async function setLogoUrl(id, logoUrl) {
  return updateDoc(doc(db, "schools", id), {
    logoUrl: logoUrl || "",
    updatedAt: serverTimestamp(),
  });
}

/** Soft delete (keep record, disable) */
export async function softDeleteSchool(id) {
  return updateDoc(doc(db, "schools", id), {
    active: false,
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update admin credentials (Firestore fields only)
 * Also mirrors the email to /users/{uid} if the school has an admin uid.
 * ⚠️ Prefer Firebase Auth for password changes; if you must store, store a hash.
 */
export async function updateSchoolCredentials(
  id,
  { adminEmail, adminPassword } = {}
) {
  const patch = cleanUndefined({
    "admin.email": adminEmail,
    "admin.password": adminPassword, // avoid storing plaintext in production
    updatedAt: serverTimestamp(),
  });

  // Update the school doc first
  await updateDoc(doc(db, "schools", id), patch);

  // Mirror email to /users/{uid} (if we know the uid)
  const schoolSnap = await getDoc(doc(db, "schools", id));
  if (schoolSnap.exists()) {
    const adminUid = schoolSnap.data()?.admin?.uid || schoolSnap.data()?.adminUid;
    if (adminUid && adminEmail) {
      await updateDoc(doc(db, "users", adminUid), {
        email: adminEmail,
        updatedAt: serverTimestamp(),
      });
    }
  }
}

/* ──────────────────────────────────────────────────────────────────
   Create School + Admin (Auth user; preserves superadmin session)
   ────────────────────────────────────────────────────────────────── */

/**
 * Create a school, then create its admin Auth user in a secondary app
 * (so the current superadmin session remains untouched), then link:
 * - schools/{id}.admin = { uid, email, createdAt }
 * - users/{uid} = { role: "schooladmin", schoolId, status: "active", active: true, ... }
 *
 * Returns: { schoolId, admin: { email, uid } }
 */
export async function createSchoolWithAdmin({
  schoolPayload,
  adminEmail,
  adminPassword,
  adminDisplayName,
}) {
  if (!adminEmail) throw new Error("adminEmail is required");
  if (!adminPassword) throw new Error("adminPassword is required");

  // 1) Create the school
  const schoolRef = await createSchool(schoolPayload);
  const schoolId = schoolRef.id;

  // 2) Create the admin Auth user on a SECONDARY app
  const apps = getApps();
  const primary = apps[0];
  const primaryOptions = primary?.options;

  let secondaryAuth;
  if (primaryOptions) {
    const secondaryApp =
      apps.find((a) => a.name === "secondary") ||
      initializeApp(primaryOptions, "secondary");
    secondaryAuth = getAuth(secondaryApp);
  } else {
    // Fallback (may affect current session if no secondary app)
    secondaryAuth = getAuth();
  }

  const cred = await createUserWithEmailAndPassword(secondaryAuth, adminEmail, adminPassword);

  // Optional: set display name and send verification (best effort)
  try {
    if (adminDisplayName) await updateProfile(cred.user, { displayName: adminDisplayName });
    await sendEmailVerification(cred.user);
  } catch {
    /* ignore */
  }

  // Clean up the secondary session
  try { await signOut(secondaryAuth); } catch {}

  const uid = cred.user.uid;

  // 3) Link admin on the school doc
  await updateDoc(doc(db, "schools", schoolId), {
    admin: {
      uid,
      email: adminEmail,
      createdAt: serverTimestamp(),
    },
    adminUid: uid,
    adminEmail: adminEmail,
    updatedAt: serverTimestamp(),
  });

  // 4) Create the user profile document for role-based routing
  //    (exact shape you requested; include active + timestamps)
  await setDoc(
    doc(db, "users", uid),
    {
      role: "schooladmin",
      schoolId,
      status: "active",       // ← string status (source of truth in your rules)
      active: true,           // ← legacy boolean, kept in sync
      email: adminEmail,
      displayName: adminDisplayName || "",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { schoolId, admin: { email: adminEmail, uid } };
}
